import clsx from 'clsx';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Box } from '@mui/material';

import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { InstantWinEvent } from 'models/instantWinEvent';
import { ReduxState } from 'ducks';
import { InstantWin } from 'models/instantWin';
import { exchangeInstantWinEventGift } from 'ducks/client/instantWinEvent';

import { GiftRedemptionModal } from './GiftRedemptionModal';
import styles from './Prize.module.css';

interface Props {
  onClose: () => void;
  instantWin: InstantWin;
  instantWinEvent: InstantWinEvent;
  tierKey: string;
}

export const Gift = ({ onClose, instantWin, instantWinEvent, tierKey }: Props): any => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { apiKey } = React.useContext(ApiKeyContext);

  const [openModal, setOpenModal] = React.useState<boolean>(false);

  const tier = instantWin?.tiers?.find((tier) => tier?.key === tierKey);
  const keyVisual = tier?.thumbnail_url ?? '';
  const title = tier?.title ?? '';
  const description = tier?.description ?? '';

  const exchanged = Boolean(instantWinEvent.gift_exchanged_date_time_utc);

  const loading = useSelector((state: ReduxState) => state.instantWinEvent.loading);

  const redemptionHandler = () => {
    dispatch(exchangeInstantWinEventGift(apiKey, instantWinEvent.id as string, i18n.language));
    setOpenModal(false);
  };

  return (
    <>
      <div className={styles.ticketContainer}>
        <div className={styles.congratsSection}>
          <div className={`${styles.divider} ${''}`} />
          <div className={styles.congratsText}>Congratulations!</div>
          <div className={`${styles.divider} ${''}`} />
        </div>
        <div className={styles.ticketPreview}>
          <img src={keyVisual} alt="e-ticket" className={styles.ticketImage} />
          {exchanged && (
            <div className={clsx(styles['gift__item__pic__stamp'])}>
              <i></i>
              {t('Redeemed')}
            </div>
          )}
          <h2 className={styles.ticketName}>{title}</h2>
        </div>
      </div>

      <div className={styles.ticketContainer}>
        <div className={styles.ticketTitle}>{description}</div>
        <div className={styles.buttonGroup}>
          <button
            className={clsx(styles.primaryButton, exchanged ? styles.grayButton : '')}
            disabled={exchanged || loading}
            onClick={() => setOpenModal(true)}
          >
            {loading ? (
              <Box width="100%" display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <>{exchanged ? t('Exchanged') : t('Exchange')}</>
            )}
          </button>
          <button className={styles.secondaryButton} onClick={onClose}>
            {t('Close')}
          </button>
        </div>
      </div>
      {openModal && (
        <GiftRedemptionModal
          open={true}
          onClose={() => setOpenModal(false)}
          giftName={title}
          onClick={redemptionHandler}
          submitting={exchanged}
        />
      )}
    </>
  );
};
